import { IconButton } from '@mui/material';
import { Fragment, useEffect, useRef, useState } from 'react';
import Joyride, { ACTIONS, CallBackProps, EVENTS, STATUS, Step } from 'react-joyride';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import HelpCenterIcon from '@mui/icons-material/HelpCenter';
import { useTranslation } from 'react-i18next';

import { useWebTourCheckUserRead, useWebTourRegisterUserRead } from './api/useWebTourController';
import { useFeatureFlags } from 'features/seismic/hooks/useFeatureFlags';

export function SeismicWebTour() {
    const initialStep = 0;
    const { t } = useTranslation();

    const [runTour, setRunTour] = useState<boolean>(false);

    const [ stepIndex, setStepIndex ] = useState<number>(0);

    const eventRef = useRef<string>('');

    const handleJoyrideCallback = (data: CallBackProps) => {
        const { status, type, action } = data;
        const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

        if (action === ACTIONS.START) {
            const joyrideElement = document.getElementById('react-joyride:0');
            if (!!joyrideElement) {
                (joyrideElement.children[1] as HTMLElement).style.zIndex = '99999';
            }
        }

        if (finishedStatuses.includes(status) && type === 'tour:end' ) {
            setRunTour(false);
            setStepIndex(0);
            if (firstTime.current) {
                firstTime.current = false;
            }
        }
        if (([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND] as string[]).includes(type)) {
            // Update state to advance the tour
            setStepIndex(index => index + (action === ACTIONS.PREV ? -1 : 1));
        }

    };

    const featureFlags = useFeatureFlags();

    const steps: Step[] = [
        {
            target: '#help',
            content: t('webTour.clickToReview'),
            disableBeacon: true,
        },
        {
            target: '#metadata',
            content: t('webTour.metadata'),
            disableBeacon: true
        },
        {
            target: '#colorbar',
            content: t('webTour.colorbar'),
            disableBeacon: true
        },
        {
            target: '#colorbar_inv',
            content: t('webTour.polarity'),
            disableBeacon: true
        },
        {
            target: '#scalebar',
            content: t('webTour.scale'),
            disableBeacon: true
        },
        {
            target: '#invert_direction',
            content: t('webTour.invertDirection'),
            disableBeacon: true
        },
        {
            target: '#apply_antialias',
            content: t('webTour.applyAntialias'),
            disableBeacon: true
        },
        {
            target: '#line-selector-type',
            content: t(!featureFlags.featureFlags?.seismic2D.TimeSlice ? 'webTour.lineSelectorType' : 'webTour.lineSelectorTypeWithZSlice'),
            disableBeacon: true
        },
        {
            target: '#line-selector-3d-skip',
            content: t('webTour.lineSelector3DSkip') ,
            disableBeacon: true
        },
        {
            target: '#trace-range-selector',
            content: t('webTour.traceRangeSelector'),
            disableBeacon: true,
        },
        {
            target: '#gains',
            content: t('webTour.gain'),
            disableBeacon: true
        },
        {
            target: '#trace-header',
            content: t('webTour.traceHeader'),
            disableBeacon: true
        },
        {
            target: '#graticule',
            content: t('webTour.graticule'),
            disableBeacon: true
        },
        {
            target: '#wells',
            content: t('webTour.wells'),
            disableBeacon: true
        },
        {
            target: '#projections',
            content: t('webTour.projections'),
            disableBeacon: true
        },
        {
            target: '#attributes',
            content: t('webTour.attributes'),
            disableBeacon: true
        },
        {
            target: '#interpretation',
            content: t('webTour.interpretations'),
            disableBeacon: true
        },
        {
            target: '#miniMap',
            content: t('webTour.miniMap'),
            disableBeacon: true,
        },
        {
            target: '#open-on-3d-viewer-btn',
            content: 'Click in the 3D Viewer Button to open the selected volume on 3D Viewer.',
            disableBeacon: true,
        },
        {
            target: '#map',
            content: t('webTour.viewerPanel1'),
            disableBeacon: true,
            placement: 'center'
        },
        {
            target: '#map',
            content: t('webTour.viewerPanel2'),
            disableBeacon: true,
            placement: 'center'
        },
        {
            target: '#map',
            content: t('webTour.viewerPanel3'),
            disableBeacon: true,
            placement: 'center'
        },
        {
            target: '#gains',
            content: t('webTour.shortcuts2'),
            disableBeacon: true,
            placement: 'center'
        },
        {
            target: '#line-selector-3d-skip',
            content: t('webTour.shortcuts1'),
            disableBeacon: true,
            placement: 'center',
        },
    ];

    const firstTime = useRef<boolean>(false);

    const webTourType = 'seismic-viewer-2d';
    const { data: dataCheckUserRead } = useWebTourCheckUserRead(webTourType);
    const { data: dataRegisterUserRead } = useWebTourRegisterUserRead(webTourType, firstTime.current);

    useEffect(() => {
        if (dataCheckUserRead === false) {
            setRunTour(true);
            firstTime.current = true;
        }
    }, [dataCheckUserRead]);

    return (
        <Fragment>
            <Joyride
                callback={handleJoyrideCallback}
                continuous
                locale={{
                    back: t('webTour.back'),
                    last: t('webTour.last'),
                    next: t('webTour.next'),
                    skip: t('webTour.skip')
                }}
                hideCloseButton
                run={runTour}
                scrollToFirstStep
                showProgress={false}
                showSkipButton
                steps={steps}
                stepIndex={stepIndex}
                styles={{
                    options: {
                        arrowColor: 'rgb(4, 31, 60)',
                        primaryColor: '#000',
                        zIndex: 99999,
                    }
                }}
            />
            <Fragment>
                <IconButton
                    id='help'
                    style={{color: 'rgba(255, 255, 255, 1)'}}
                    onClick={() => setRunTour(true)}
                    title='Start the guide tour'>
                    <HelpCenterIcon style={{fontSize:'24px'}}/>
                </IconButton>
            </Fragment>
        </Fragment>
    );
}